import React, { useState } from "react";
import { MessageSquare, Send, User } from "lucide-react";
import { api } from "../utils/api";

const ROLE_COLOR = {
  admin:       { ring: "ring-slate-300",   dot: "bg-slate-600",   chip: "bg-slate-100 text-slate-700" },
  user:        { ring: "ring-slate-200",   dot: "bg-slate-400",   chip: "bg-slate-50 text-slate-600" },
  it_team:     { ring: "ring-sky-200",     dot: "bg-sky-500",     chip: "bg-sky-50 text-sky-700" },
  it_manager:  { ring: "ring-emerald-200", dot: "bg-emerald-600", chip: "bg-emerald-50 text-emerald-700" },
  it_security: { ring: "ring-violet-200",  dot: "bg-violet-600",  chip: "bg-violet-50 text-violet-700" },
  cfo:         { ring: "ring-amber-200",   dot: "bg-amber-600",   chip: "bg-amber-50 text-amber-700" },
};

function fmt(dt) {
  const d = new Date(dt);
  return d.toLocaleString(undefined, {
    year: "numeric", month: "short", day: "2-digit",
    hour: "2-digit", minute: "2-digit",
  });
}

function initials(name) {
  return (name || "?")
    .split(/\s+/).filter(Boolean).slice(0, 2)
    .map((w) => w[0]).join("").toUpperCase();
}

/**
 * CommentThread — persistent date/time-stamped comments for any ticket.
 * Props:
 *   kind: 'incidents' | 'service-requests' | 'change-requests' | 'problems'
 *   ticket: full ticket object (must include `comments` array)
 *   onPosted: optional callback after successful post
 */
export default function CommentThread({ kind, ticket, onPosted }) {
  const [text, setText] = useState("");
  const [posting, setPosting] = useState(false);
  const comments = (ticket?.comments || []).slice().sort(
    (a, b) => new Date(a.created_at) - new Date(b.created_at)
  );

  async function post() {
    const t = text.trim();
    if (!t) return;
    setPosting(true);
    try {
      await api.post(`/api/${kind}/${ticket.id}/comments`, { text: t });
      setText("");
      onPosted?.();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to post comment");
    } finally {
      setPosting(false);
    }
  }

  function onKey(e) {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) post();
  }

  return (
    <div className="card p-5">
      <h2 className="font-semibold mb-3 flex items-center gap-2 text-gray-800">
        <MessageSquare size={16} className="text-brand-500" />
        Comments
        {comments.length > 0 && (
          <span className="text-xs font-normal text-gray-500">({comments.length})</span>
        )}
      </h2>

      {comments.length === 0 ? (
        <p className="text-sm text-gray-500 italic mb-4">No comments yet — add the first one below.</p>
      ) : (
        <ol className="space-y-3 mb-5 max-h-[480px] overflow-y-auto pr-2">
          {comments.map((c) => {
            const c_color = ROLE_COLOR[c.author_role] || ROLE_COLOR.user;
            return (
              <li key={c.id} className="flex gap-3">
                <div className={`flex-shrink-0 w-9 h-9 rounded-full ${c_color.dot} text-white text-xs font-bold flex items-center justify-center ring-2 ${c_color.ring}`}>
                  {initials(c.author_name)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center flex-wrap gap-2 text-xs">
                    <span className="font-semibold text-gray-800">{c.author_name}</span>
                    <span className={`badge ${c_color.chip}`}>{c.author_role}</span>
                    <span className="text-gray-500" title={new Date(c.created_at).toISOString()}>
                      {fmt(c.created_at)}
                    </span>
                  </div>
                  <div className="mt-1 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 whitespace-pre-wrap break-words">
                    {c.text}
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      )}

      <div className="border-t pt-4">
        <label className="label flex items-center gap-1.5">
          <User size={12} /> Add a comment
        </label>
        <textarea
          className="input"
          rows="3"
          placeholder="Type your comment… (Ctrl/⌘+Enter to send)"
          value={text}
          onKeyDown={onKey}
          onChange={(e) => setText(e.target.value)}
        />
        <div className="flex items-center justify-between mt-2">
          <span className="text-xs text-gray-500">{text.length}/4000</span>
          <button
            onClick={post}
            disabled={posting || !text.trim()}
            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Send size={14} />
            {posting ? "Posting…" : "Post comment"}
          </button>
        </div>
      </div>
    </div>
  );
}
